import { z } from 'zod';

import {
  instanceActivitySchema,
  instancePhaseSpecSchema,
  phasedInstanceSpecSchema,
} from './instance-spec';
import type {
  InstanceActivitySpec,
  InstancePhaseSpec,
  PhasedInstanceSpec,
} from './instance-spec';
import { reassessmentMarkerSchema } from './reassessment-marker';
import { frequencySegmentSchema } from './session-frequency';
import {
  activityDosageSchema,
  cadenceSchema,
  codedConceptSchema,
  controlBandSchema,
  goalSchema,
  goalTargetSchema,
  phaseSchema,
  planActivitySchema,
  planEventSchema,
  planSpecSchema,
} from './spec';
import type { PlanSpec } from './spec';

// Streaming-tolerant decode of an IN-FLIGHT Plan Spec. While present_plan's
// tool call streams, the UI receives a progressively-parsed prefix of the
// spec (partial JSON). This coerces that prefix into a valid PlanSpec by
// keeping every entry that already parses and dropping the half-streamed
// tail — so the preview pane can render the draft building live. Returns
// null until enough of the spine exists to render at all. Idempotent on
// complete specs.

type Raw = Record<string, unknown>;

function asObject(value: unknown): Raw | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Raw)
    : null;
}

// A half-streamed optional sub-object (e.g. a cadence missing its periodUnit)
// must not sink its parent — degrade it to undefined until it completes.
function parsedOrUndefined<S extends z.ZodTypeAny>(
  schema: S,
  value: unknown,
): z.infer<S> | undefined {
  if (value === undefined) {
    return undefined;
  }
  const parsed = schema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

function keepParsed<S extends z.ZodTypeAny>(
  schema: S,
  items: unknown,
): z.infer<S>[] {
  if (!Array.isArray(items)) {
    return [];
  }
  const out: z.infer<S>[] = [];
  for (const item of items) {
    const parsed = schema.safeParse(item);
    if (parsed.success) {
      out.push(parsed.data);
    }
  }
  return out;
}

function sanitizeActivities(
  items: unknown,
): z.infer<typeof planActivitySchema>[] {
  if (!Array.isArray(items)) {
    return [];
  }
  const out: z.infer<typeof planActivitySchema>[] = [];
  for (const item of items) {
    const obj = asObject(item);
    if (!obj) {
      continue;
    }
    const parsed = planActivitySchema.safeParse({
      ...obj,
      code: parsedOrUndefined(codedConceptSchema, obj['code']),
      dosage: parsedOrUndefined(activityDosageSchema, obj['dosage']),
      frequency: parsedOrUndefined(cadenceSchema, obj['frequency']),
    });
    if (parsed.success) {
      out.push(parsed.data);
    }
  }
  return out;
}

function sanitizeGoals(items: unknown): z.infer<typeof goalSchema>[] {
  if (!Array.isArray(items)) {
    return [];
  }
  const out: z.infer<typeof goalSchema>[] = [];
  for (const item of items) {
    const obj = asObject(item);
    if (!obj) {
      continue;
    }
    const parsed = goalSchema.safeParse({
      ...obj,
      targets:
        obj['targets'] === undefined
          ? undefined
          : keepParsed(goalTargetSchema, obj['targets']),
    });
    if (parsed.success) {
      out.push(parsed.data);
    }
  }
  return out;
}

function sanitizePhases(items: unknown): z.infer<typeof phaseSchema>[] {
  if (!Array.isArray(items)) {
    return [];
  }
  const out: z.infer<typeof phaseSchema>[] = [];
  for (const item of items) {
    const obj = asObject(item);
    if (!obj) {
      continue;
    }
    const parsed = phaseSchema.safeParse({
      ...obj,
      cadence: parsedOrUndefined(cadenceSchema, obj['cadence']),
      activities:
        obj['activities'] === undefined
          ? undefined
          : sanitizeActivities(obj['activities']),
    });
    if (parsed.success) {
      out.push(parsed.data);
    }
  }
  return out;
}

function sanitizeEvents(items: unknown): z.infer<typeof planEventSchema>[] {
  if (!Array.isArray(items)) {
    return [];
  }
  const out: z.infer<typeof planEventSchema>[] = [];
  for (const item of items) {
    const obj = asObject(item);
    if (!obj) {
      continue;
    }
    const parsed = planEventSchema.safeParse({
      ...obj,
      activities:
        obj['activities'] === undefined
          ? undefined
          : sanitizeActivities(obj['activities']),
    });
    if (parsed.success) {
      out.push(parsed.data);
    }
  }
  return out;
}

export function coercePartialPlanSpec(value: unknown): PlanSpec | null {
  const obj = asObject(value);
  if (!obj || typeof obj['title'] !== 'string' || obj['title'].length === 0) {
    return null;
  }

  const shared = {
    title: obj['title'],
    description:
      typeof obj['description'] === 'string' ? obj['description'] : undefined,
    condition: parsedOrUndefined(codedConceptSchema, obj['condition']),
    goals: obj['goals'] === undefined ? undefined : sanitizeGoals(obj['goals']),
  };

  let candidate: unknown;
  switch (obj['shape']) {
    case 'phased':
      candidate = {
        shape: obj['shape'],
        ...shared,
        phases: sanitizePhases(obj['phases']),
      };
      break;
    case 'episodic':
      candidate = {
        shape: obj['shape'],
        ...shared,
        baseline:
          typeof obj['baseline'] === 'string' ? obj['baseline'] : undefined,
        baselineActivities:
          obj['baselineActivities'] === undefined
            ? undefined
            : sanitizeActivities(obj['baselineActivities']),
        events: sanitizeEvents(obj['events']),
      };
      break;
    case 'continuous':
      candidate = {
        shape: obj['shape'],
        ...shared,
        bands: keepParsed(controlBandSchema, obj['bands']),
        cadence: parsedOrUndefined(cadenceSchema, obj['cadence']),
        reviewRule:
          typeof obj['reviewRule'] === 'string' ? obj['reviewRule'] : undefined,
        activities:
          obj['activities'] === undefined
            ? undefined
            : sanitizeActivities(obj['activities']),
      };
      break;
    default:
      // Shape discriminator not streamed yet (or unknown) — nothing to render.
      return null;
  }

  // The spine minimums (≥1 phase / ≥1 event / ≥1 band, episodic baseline)
  // gate here: until they stream in, callers show a skeleton instead.
  const parsed = planSpecSchema.safeParse(candidate);
  return parsed.success ? parsed.data : null;
}

// --- Plan Instance Spec (the Care Plan Tailor's living draft) -----------------

function sanitizeInstanceActivities(items: unknown): InstanceActivitySpec[] {
  if (!Array.isArray(items)) {
    return [];
  }
  const out: InstanceActivitySpec[] = [];
  for (const item of items) {
    const obj = asObject(item);
    if (!obj) {
      continue;
    }
    const parsed = instanceActivitySchema.safeParse({
      ...obj,
      code: parsedOrUndefined(codedConceptSchema, obj['code']),
      dosage: parsedOrUndefined(activityDosageSchema, obj['dosage']),
      frequency: parsedOrUndefined(cadenceSchema, obj['frequency']),
    });
    if (parsed.success) {
      out.push(parsed.data);
    }
  }
  return out;
}

function sanitizeInstancePhases(items: unknown): InstancePhaseSpec[] {
  if (!Array.isArray(items)) {
    return [];
  }
  const out: InstancePhaseSpec[] = [];
  for (const item of items) {
    const obj = asObject(item);
    if (!obj) {
      continue;
    }
    const parsed = instancePhaseSpecSchema.safeParse({
      ...obj,
      activities:
        obj['activities'] === undefined
          ? undefined
          : sanitizeInstanceActivities(obj['activities']),
    });
    if (parsed.success) {
      out.push(parsed.data);
    }
  }
  return out;
}

// The instance-spec twin of coercePartialPlanSpec: tolerant decode of an
// in-flight present_plan_instance emission, so the Tailor's review pane renders
// the patient's draft building live as the tool input streams.
export function coercePartialPlanInstanceSpec(
  value: unknown,
): PhasedInstanceSpec | null {
  const obj = asObject(value);
  if (!obj || obj['shape'] !== 'phased') {
    return null;
  }
  if (typeof obj['title'] !== 'string' || obj['title'].length === 0) {
    return null;
  }
  if (
    typeof obj['templateCanonical'] !== 'string' ||
    typeof obj['startDate'] !== 'string'
  ) {
    return null;
  }

  const parsed = phasedInstanceSpecSchema.safeParse({
    shape: obj['shape'],
    title: obj['title'],
    templateCanonical: obj['templateCanonical'],
    phaseCodeSystem:
      typeof obj['phaseCodeSystem'] === 'string'
        ? obj['phaseCodeSystem']
        : undefined,
    startDate: obj['startDate'],
    goals: obj['goals'] === undefined ? undefined : sanitizeGoals(obj['goals']),
    // Omitting these left the streaming preview showing a blank schedule for a
    // draft that has one.
    sessionFrequency: parsedOrUndefined(
      z.array(frequencySegmentSchema),
      obj['sessionFrequency'],
    ),
    reassessment: parsedOrUndefined(
      reassessmentMarkerSchema,
      obj['reassessment'],
    ),
    phases: sanitizeInstancePhases(obj['phases']),
  });
  return parsed.success ? parsed.data : null;
}
